import { useCallback, useState } from 'react';

import { type ResolvedSection } from '../resolve-sections/resolve-sections';

export interface UseDynamicFormSectionLogicInput {
  section: ResolvedSection;
  isInvalid: boolean;
}

export interface UseDynamicFormSectionLogic {
  isOpen: boolean;
  setOpen: (isOpen: boolean) => void;
}

/**
 * Whether a collapsible section is folded.
 *
 * The rule the whole thing exists for: **an invalid section is never folded.**
 * `isOpen` is therefore `open || isInvalid` rather than state the error writes
 * into — a `setState` in an effect is both forbidden here (React Compiler's
 * `set-state-in-effect`) and wrong, because it would let the operator fold the
 * error away again on the very next click.
 *
 * A section that is not collapsible has no state at all: it is always open.
 */
export function useDynamicFormSectionLogic({
  section,
  isInvalid,
}: UseDynamicFormSectionLogicInput): UseDynamicFormSectionLogic {
  const [isOpen, setOpen] = useState(section.isInitiallyOpen);

  const handleOpenChange = useCallback((next: boolean) => {
    setOpen(next);
  }, []);

  if (!section.isCollapsible) {
    return { isOpen: true, setOpen: handleOpenChange };
  }

  return { isOpen: isOpen || isInvalid, setOpen: handleOpenChange };
}
